import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Mic, Sparkles, Square } from "lucide-react";
import { toast } from "sonner";
import FounderReview from "@/components/workspace/FounderReview";
import { Link } from "react-router-dom";
import { api, errorText } from "@/lib/api";

export function useRecorder({ onText, t }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorder = useRef(null);
  const chunks = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunks.current, { type: mime });
        if (!blob.size) return;
        setTranscribing(true);
        const form = new FormData();
        form.append("file", blob, mime === "audio/webm" ? "recording.webm" : "recording.mp4");
        try { const r = await api.post("/chat/transcribe", form); onText(r.data.text); }
        catch (err) { toast.error(errorText(err, "Could not transcribe the audio.")); }
        finally { setTranscribing(false); }
      };
      rec.start();
      recorder.current = rec;
      setRecording(true);
    } catch { toast.error(t.micDenied); }
  };
  const stop = () => { recorder.current?.stop(); setRecording(false); };
  return { recording, transcribing, start, stop };
}

export default function Chat({ lang, t, data, setData }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingReview, setPendingReview] = useState(false);
  const [historyLimit, setHistoryLimit] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  const historyLimitRef = useRef(50);
  const bottom = useRef(null);
  const { recording, transcribing, start, stop } = useRecorder({ t, onText: (text) => setMessage((m) => (m ? `${m} ${text}` : text)) });

  useEffect(() => { if (historyLimitRef.current === 50) bottom.current?.scrollIntoView?.({ behavior: "smooth" }); }, [data.chat?.length]);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const [status, history] = await Promise.all([api.get("/chat/review-status"), api.get("/chat/history?limit=" + historyLimit)]);
        if (!active) return;
        setPendingReview(Boolean(status.data?.pending));
        setHasMore(Boolean(history.data?.has_more));
        setData((d) => ({ ...d, chat: history.data?.messages || [] }));
      } catch { /* Keep the existing conversation when a refresh is unavailable. */ }
    };
    if (!sending) refresh();
    const timer = setInterval(() => { if (!sending) refresh(); }, 15000);
    return () => { active = false; clearInterval(timer); };
  }, [sending, setData, historyLimit]);

  const send = async (e) => {
    e.preventDefault();
    const text = message.trim();
    if (!text || sending) return;
    setSending(true);
    setData((d) => ({ ...d, chat: [...(d.chat || []), { role: "user", content: text }] }));
    setMessage("");
    try {
      const startedAt = Date.now();
      const r = await api.post("/chat", { message: text });
      const answer = r.data;
      if (r.status === 202 || !answer.content) { setPendingReview(true); return; }
      const delay = Math.min(120000, Math.max(3000, String(answer.content).length * 35));
      const remaining = Math.max(0, delay - (Date.now() - startedAt));
      if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
      setData((d) => ({ ...d, chat: [...(d.chat || []), answer] }));
    } catch (err) {
      toast.error(errorText(err, "Message could not be sent."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="chat-page">
      {data.user?.is_advisor && <FounderReview lang={lang} mode="identity" />}
      <div className="chat-intro"><div className="advisor-mark"><Sparkles size={18} /></div><div><p className="eyebrow">NOORIN</p><h2>{t.advisor}</h2><p>{t.empty}</p></div></div>
      <div className="messages" data-testid="chat-messages">
      {hasMore && <button type="button" onClick={() => setHistoryLimit((n) => { historyLimitRef.current = n + 50; return n + 50; })}>{lang === "fa" ? "نمایش پیام‌های قدیمی‌تر" : "Load older messages"}</button>}
        {(data.chat || []).map((m, i) => <div className={`message ${m.role}`} key={i} data-testid={`chat-message-${m.role}`}><span className="message-role">{m.role === "advisor" ? "NOORIN" : (lang === "fa" ? "شما" : "You")}</span><p>{m.content}</p></div>)}
        {sending && <div className="message advisor thinking" data-testid="chat-thinking"><span className="message-role">NOORIN</span><p>…</p></div>}
        <div ref={bottom} />
      </div>
      {pendingReview && <small className="muted" role="status">{lang === "fa" ? "در انتظار بررسی مؤسس" : "Awaiting founder review"}</small>}
      <Link to="/workspace/growth-club" className="quiet-button" data-testid="strategy-growth-club">{lang === "fa" ? "ادامه همکاری در باشگاه رشد" : "Continue in Growth Club"}</Link>
      <form className="chat-composer" onSubmit={send}>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={transcribing ? t.transcribing : t.placeholder} data-testid="chat-message-input" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} />
        <div className="composer-actions">
          <button type="button" className={`mic-button ${recording ? "recording" : ""}`} onClick={recording ? stop : start} disabled={transcribing} title={recording ? t.stop : t.record} aria-label={recording ? t.stop : t.record} data-testid="chat-mic-button">{recording ? <Square size={16} /> : <Mic size={17} />}</button>
          <button className="primary-button" disabled={sending || !message.trim()} data-testid="chat-send-button">{sending ? "…" : t.send}<ArrowUpRight size={17} /></button>
        </div>
      </form>
    </div>
  );
}
